'use client';

import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import { useTranslations } from 'next-intl';
import { useQuickToast } from 'hooks/use-toast';
import {
  AddToCartApiCall,
  getPaymentMethods
} from 'services/parts-diagram/actions';
import { useAddToCartEvent } from 'hooks/analytics/use-add-to-cart-event';
import { PARTS_DIAGRAM_PAGE } from 'constants/analyticsConstants';
import {
  CATEGORY_TYPES,
  ComponentsType,
  LineItem,
  PartDetailsAndSchematicResponse,
  PartItem,
  AnalyticsCommonInformation
} from 'types/parts-diagram';

interface PartDiagramContext {
  isToggle: boolean;
  handleToggle: () => void;
  displayCategory: CATEGORY_TYPES;
  handlePartsContentCategory: (
    category: CATEGORY_TYPES,
    components?: ComponentsType | null,
    partsDetails?: PartDetailsAndSchematicResponse
  ) => void;
  componentsData: ComponentsType;
  partsDetails: PartDetailsAndSchematicResponse;
  selectedPart: PartItem;
  handleSelectedPart: (partItem: PartItem) => void;
  isCatCorp: boolean;
  setIsCatCorp: Dispatch<SetStateAction<boolean>>;
  handleAddToCart: (
    part: LineItem,
    quantity: string,
    analyticsInformation: AnalyticsCommonInformation
  ) => void;
  equipmentError: boolean;
  setEquipmentError: Dispatch<SetStateAction<boolean>>;
  disableAddToCart: boolean;
  serverData;
  setServerData;
  clickedLineItem: LineItem;
  setClickedLineItem: Dispatch<SetStateAction<LineItem>>;
  analyticsCommonInfo?: AnalyticsCommonInformation;
  setAnalyticsCommonInfo?: Dispatch<SetStateAction<AnalyticsCommonInformation>>;
}

const PartDiagramContext = createContext<PartDiagramContext>(null);
export const usePartDiagram = () => useContext(PartDiagramContext);

export const PartDiagramContextProvider = ({ children }) => {
  const t = useTranslations();
  const toast = useQuickToast();
  const [isToggle, setIsToggle] = useState(false);
  const [displayCategory, setDisplayCategory] = useState<CATEGORY_TYPES>(
    CATEGORY_TYPES.LISTOFARRANGEMENTS
  );
  const [componentsData, setComponentsData] = useState<ComponentsType>();
  const [partsDetails, setPartsDetails] =
    useState<PartDetailsAndSchematicResponse>();
  const [selectedPart, setSelectedPart] = useState<PartItem | null>(null);
  const [isCatCorp, setIsCatCorp] = useState<boolean>(true);
  const [equipmentError, setEquipmentError] = useState<boolean>(false);
  const [disableAddToCart, setDisableAddToCart] = useState<boolean>(false);
  const [serverData, setServerData] = useState();

  const [analyticsCommonInfo, setAnalyticsCommonInfo] =
    useState<AnalyticsCommonInformation>({});
  const [clickedLineItem, setClickedLineItem] = useState<LineItem>();

  const handleToggle = useCallback(() => setIsToggle(!isToggle), [isToggle]);

  const { fireEvent: fireEventAddToCart } = useAddToCartEvent();

  const handleSelectedPart = useCallback(
    (partItem: PartItem) => setSelectedPart(partItem),
    []
  );

  const handlePartsContentCategory = (
    category: CATEGORY_TYPES,
    componentsData?: ComponentsType,
    partsDetails?: PartDetailsAndSchematicResponse
  ) => {
    setDisplayCategory(category);
    switch (category) {
      case CATEGORY_TYPES.PARTSCARDS:
        setComponentsData(componentsData);
        break;
      case CATEGORY_TYPES.PARTSDETAILS:
        setPartsDetails(partsDetails);
        break;
    }
  };

  async function handleAddToCart(
    part: LineItem,
    quantity: string,
    analyticsInformation: AnalyticsCommonInformation
  ) {
    setDisableAddToCart(true);
    const partItem = {
      ...part,
      quantity: quantity,
      sosCode: part?.priceAndAvailabilityData?.sosCode
    };
    const {
      currency,
      dealerData: { id: dealerId, dealerName },
      model
    } = analyticsInformation;
    const { error, response } = await AddToCartApiCall(partItem);
    if (error) {
      toast.error({
        message: t('ITEM_ADD_ERROR')
      });
    } else {
      toast.success({
        message: t('ITEM_ADDED_TO_CART')
      });

      const { orderId } = response;
      const { paymentMethods } = await getPaymentMethods(orderId);

      if (paymentMethods) {
        fireEventAddToCart({
          cartId: orderId,
          currencyCode: currency,
          dealerId,
          model,
          product: partItem,
          storeName: dealerName,
          paymentMethods,
          list: PARTS_DIAGRAM_PAGE
        });
      }
    }
    setDisableAddToCart(false);
  }

  const contextValues = useMemo(() => {
    return {
      isToggle,
      handleToggle,
      displayCategory,
      handlePartsContentCategory,
      componentsData,
      partsDetails,
      selectedPart,
      handleSelectedPart,
      isCatCorp,
      setIsCatCorp,
      handleAddToCart,
      equipmentError,
      setEquipmentError,
      disableAddToCart,
      serverData,
      setServerData,
      analyticsCommonInfo,
      setAnalyticsCommonInfo,
      clickedLineItem,
      setClickedLineItem
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isToggle,
    handleToggle,
    displayCategory,
    componentsData,
    partsDetails,
    selectedPart,
    handleSelectedPart,
    isCatCorp,
    setIsCatCorp,
    equipmentError,
    setEquipmentError,
    disableAddToCart,
    serverData,
    setServerData,
    analyticsCommonInfo,
    setAnalyticsCommonInfo,
    clickedLineItem,
    setClickedLineItem
  ]);

  return (
    <PartDiagramContext.Provider value={contextValues}>
      {children}
    </PartDiagramContext.Provider>
  );
};
